import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { DataProvider } from "./contexts/DataContext";
import OnboardingHost from "./components/onboarding/OnboardingHost";
import WhatsAppFab from "./components/support/WhatsAppFab";
import QARecorderWidget from "./components/qa/QARecorderWidget";
import PermissionRoute from "./components/PermissionRoute";
import SeoHreflang from "./components/seo/SeoHreflang";

// Lazy-loaded page components
const Homepage = lazy(() => import("pages/homepage"));
const DocsHub = lazy(() => import("pages/docs"));
const UserAuthentication = lazy(() => import("pages/user-authentication"));
const IndustryBlog = lazy(() => import("pages/industry-blog"));
const ArticleDetail = lazy(() => import("pages/article-detail"));
const AdvancedSearchResults = lazy(() => import("pages/advanced-search-results"));
const BusinessRegistration = lazy(() => import("pages/business-registration"));
const PlansPage = lazy(() => import("pages/plans"));
const BusinessProfileDetail = lazy(() => import("pages/business-profile-detail"));
const Dashboard = lazy(() => import("pages/dashboard"));
const FavoritesPage = lazy(() => import("pages/dashboard/components/FavoritesPage"));
const BusinessProfilePage = lazy(() => import("pages/dashboard/components/BusinessProfilePage"));
const BusinessQuickEditPage = lazy(() => import("pages/dashboard/components/BusinessQuickEditPage"));
const BusinessVerificationPage = lazy(() => import("pages/dashboard/components/BusinessVerificationPage"));
const BusinessCertificationsPage = lazy(() => import("pages/dashboard/components/BusinessCertificationsPage"));
const BusinessSocialNetworksPage = lazy(() => import("pages/dashboard/components/BusinessSocialNetworksPage"));
const BusinessListPage = lazy(() => import("pages/dashboard/components/BusinessListPage"));
const BusinessReviewsDashboard = lazy(() => import("pages/dashboard/components/BusinessReviewsDashboard"));
const BusinessLeads = lazy(() => import("pages/dashboard/components/BusinessLeads"));
const BusinessAdminsPage = lazy(() => import("pages/dashboard/components/BusinessAdminsPage"));
const ServicesPage = lazy(() => import("pages/dashboard/components/ServicesPage"));
const ProductsPage = lazy(() => import("pages/dashboard/components/ProductsPage"));
const BusinessAnalytics = lazy(() => import("pages/dashboard/components/BusinessAnalytics"));
const ReviewsPage = lazy(() => import("pages/dashboard/components/ReviewsPage"));
const LeadsPage = lazy(() => import("pages/dashboard/components/LeadsPage"));
const CommunityActivityPage = lazy(() => import("pages/dashboard/components/CommunityActivityPage"));
const EditProfilePage = lazy(() => import("pages/dashboard/components/EditProfilePage"));
const QuotesPage = lazy(() => import("pages/dashboard/components/QuotesPage"));
const BusinessQuotes = lazy(() => import("pages/dashboard/components/BusinessQuotes"));
const AdminDashboard = lazy(() => import("pages/admin/AdminDashboard"));
const ProviderJobs = lazy(() => import("pages/dashboard/components/ProviderJobsPage"));
const BusinessBlogPage = lazy(() => import("pages/dashboard/components/BusinessBlogPage"));
const AdminBusinesses = lazy(() => import("pages/admin/AdminBusinesses"));
const AdminUsers = lazy(() => import("pages/admin/AdminUsers"));
const AdminReviews = lazy(() => import("pages/admin/AdminReviews"));
const AdminLeads = lazy(() => import("pages/admin/AdminLeads"));
const AdminContent = lazy(() => import("pages/admin/AdminContent"));
const AdminLegal = lazy(() => import("pages/admin/AdminLegal"));
const AdminFAQ = lazy(() => import("pages/admin/AdminFAQ"));
const AdminContactRequests = lazy(() => import("pages/admin/AdminContactRequests"));
const AdminNewsletter = lazy(() => import("pages/admin/AdminNewsletter"));
const AdminBlogArticles = lazy(() => import("pages/admin/AdminBlogArticles"));
const AdminBugReports = lazy(() => import("pages/admin/AdminBugReports"));
const AdminSubscriptions = lazy(() => import("pages/admin/AdminSubscriptions"));
const AdminPlatformConfig = lazy(() => import("pages/admin/AdminPlatformConfig"));
const AdminCustomFields = lazy(() => import("pages/admin/AdminCustomFields"));
const AdminAnalytics = lazy(() => import("pages/admin/AdminAnalytics"));
const AdminCategories = lazy(() => import("pages/admin/AdminCategories"));
const AdminCatalogStructure = lazy(() => import("pages/admin/AdminCatalogStructure"));
const AdminWhatsAppCommunity = lazy(() => import("pages/admin/AdminWhatsAppCommunity"));
const AdminSettings = lazy(() => import("pages/admin/AdminSettings"));
const AdminJobs = lazy(() => import("pages/admin/AdminJobs"));
const AdminQuoteRequests = lazy(() => import("pages/admin/AdminQuoteRequests"));
const AdminAssociationRequests = lazy(() => import("pages/admin/AdminAssociationRequests"));
const AdminClaimRequests = lazy(() => import("pages/admin/AdminClaimRequests"));
const AdminForum = lazy(() => import("pages/admin/AdminForum"));
const AdminQASessions = lazy(() => import("pages/admin/AdminQASessions"));
const AdminPlanRequests = lazy(() => import("pages/admin/AdminPlanRequests"));
const AssociationRequestPage = lazy(() => import("pages/dashboard/components/AssociationRequestPage"));
const SubscriptionPage = lazy(() => import("pages/dashboard/components/SubscriptionPage"));
const Jobs = lazy(() => import("pages/jobs"));
const JobDetail = lazy(() => import("pages/job-detail"));
const CreateJob = lazy(() => import("pages/jobs/CreateJob"));
const FAQPage = lazy(() => import("pages/faq"));
const LegalIndex = lazy(() => import("pages/legal-index"));
const LegalPage = lazy(() => import("pages/legal"));
const PrivacyPolicy = lazy(() => import("pages/privacy-policy"));
const TermsOfService = lazy(() => import("pages/terms-of-service"));
const CheckoutPage = lazy(() => import("pages/checkout"));
const CotizarPage = lazy(() => import("pages/cotizar"));
const PostCardVibes = lazy(() => import("pages/demo/PostCardVibes"));
const SearchCardVibes = lazy(() => import("pages/demo/SearchCardVibes"));
const DiscoveryCardVibes = lazy(() => import("pages/demo/DiscoveryCardVibes"));
const UserProfilePage = lazy(() => import("pages/user-profile"));
const SolicitudDetailPage = lazy(() => import("pages/solicitud-detail"));
const ProductDetailPage = lazy(() => import("pages/product-detail"));
const ServiceDetailPage = lazy(() => import("pages/service-detail"));
const NotFound = lazy(() => import("pages/NotFound"));
const LinkedInCallback = lazy(() => import("pages/auth/LinkedInCallback"));
const ResetPassword = lazy(() => import("pages/reset-password"));
const VerifyEmail = lazy(() => import("pages/verify-email"));
const ConfirmCorporateEmail = lazy(() => import("pages/confirm-corporate-email"));

// Minimal loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
  </div>
);

// Path-based i18n: detectar prefijo /en o /pt en el pathname y usarlo como
// basename. Así <Route path="/planes"> matchea /planes (ES default) y
// /en/planes (EN) sin duplicar rutas, y <Link to="/planes"> resuelve a
// la URL con el prefijo activo automáticamente.
const LANG_PREFIX_RE = /^\/(en|pt)(\/|$)/;
const detectLangPrefix = () => {
  if (typeof window === 'undefined') return '';
  const match = window.location.pathname.match(LANG_PREFIX_RE);
  return match ? `/${match[1]}` : '';
};
const LANG_BASENAME = detectLangPrefix();

const Routes = () => {
  return (
    <BrowserRouter basename={LANG_BASENAME || '/'}>
      <SeoHreflang />
      <DataProvider>
        <OnboardingHost />
        <WhatsAppFab />
        {import.meta.env.VITE_ENABLE_QA_RECORDER === 'true' && <QARecorderWidget />}
        <ErrorBoundary>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <RouterRoutes>
              {/* Define your routes here */}
              {/* Swap 2026-06: / ahora muestra el buscador de proveedores;
                  /homepage queda como ruta para la landing pública. */}
              <Route path="/" element={<AdvancedSearchResults />} />
              <Route path="/homepage" element={<Homepage />} />
              <Route path="/user-authentication" element={<UserAuthentication />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/auth/verify-email" element={<VerifyEmail />} />
              <Route path="/auth/confirm-corporate-email" element={<ConfirmCorporateEmail />} />
              <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
              <Route path="/industry-blog" element={<IndustryBlog />} />
              <Route path="/industry-blog/article/:slug" element={<ArticleDetail />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/jobs/create" element={<CreateJob />} />
              <Route path="/jobs/:slug" element={<JobDetail />} />
              <Route path="/advanced-search-results" element={<AdvancedSearchResults />} />
              <Route path="/comunidad" element={<CotizarPage />} />
              <Route path="/demo/postcard/:vibe" element={<PostCardVibes />} />
              <Route path="/demo/searchcard/:vibe" element={<SearchCardVibes />} />
              <Route path="/demo/discoverycard/:vibe" element={<DiscoveryCardVibes />} />
              <Route path="/usuario/:id" element={<UserProfilePage />} />
              <Route path="/solicitud/:id" element={<SolicitudDetailPage />} />
              <Route path="/producto/:id" element={<ProductDetailPage />} />
              <Route path="/servicio/:id" element={<ServiceDetailPage />} />
              <Route path="/planes" element={<PlansPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/business-registration" element={<BusinessRegistration />} />
              <Route path="/business-profile-detail" element={<BusinessProfileDetail />} />
              {/* Ruta canónica con slug — corta scrape por enumeración de IDs
                  consecutivos (?id=1..N). La vieja arriba sigue funcionando y
                  hace navigate(..., {replace: true}) al slug apenas carga el
                  biz (redirect client-side). */}
              <Route path="/empresa/:slug" element={<BusinessProfileDetail />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/favorites" element={<FavoritesPage />} />
              <Route path="/dashboard/reviews" element={<ReviewsPage />} />
              <Route path="/dashboard/leads" element={<LeadsPage />} />
              <Route path="/dashboard/community-activity" element={<CommunityActivityPage />} />
              <Route path="/dashboard/edit-profile" element={<EditProfilePage />} />
              {/* Rutas solo para dueños de empresa */}
              {/* /dashboard/business muestra owned + asociadas; cualquier
                  asociado activo puede entrar. Las acciones dentro de cada
                  card se gatean por perm en el componente (PermissionGate). */}
              <Route path="/dashboard/business" element={<PermissionRoute permission="any_business_relation"><BusinessListPage /></PermissionRoute>} />
              <Route path="/dashboard/business/:id/profile" element={<PermissionRoute permission="owner_only"><BusinessProfilePage /></PermissionRoute>} />
              {/* Editor reducido para asociados (admin_empresa / ventas /
                  marketing). El wizard largo de /profile sigue siendo
                  owner-only; quick-edit cubre los campos de marketing. */}
              <Route path="/dashboard/business/:id/quick-edit" element={<PermissionRoute permission="any_business_relation"><BusinessQuickEditPage /></PermissionRoute>} />
              {/* Verificación de docs legales — antes vivía como paso 4 del
                  wizard de registro. Se sacó del wizard porque bloqueaba
                  a owners sin los PDFs a mano. Ahora es un flujo opcional
                  siempre disponible desde el dashboard, con el mismo UI
                  de drag&drop y el mismo endpoint de upload. */}
              <Route path="/dashboard/business/:businessId/verification" element={<PermissionRoute permission="owner_only"><BusinessVerificationPage /></PermissionRoute>} />
              {/* Certificaciones — antes vivían en el paso 1 del wizard.
                  Se sacaron por el mismo motivo que verificación: son
                  opcionales y agregaban fricción al registro base. El
                  gate fino (owner + admin_empresa/ventas/marketing/
                  can_edit_business_profile) vive dentro de la página. */}
              <Route path="/dashboard/business/:businessId/certifications" element={<PermissionRoute permission="owner_only"><BusinessCertificationsPage /></PermissionRoute>} />
              <Route path="/dashboard/business/:businessId/social-networks" element={<PermissionRoute permission="owner_only"><BusinessSocialNetworksPage /></PermissionRoute>} />
              <Route path="/dashboard/business/:id/admins" element={<PermissionRoute permission="owner_only"><BusinessAdminsPage /></PermissionRoute>} />
              <Route path="/business-profile/:id" element={<PermissionRoute permission="owner_only"><BusinessProfilePage /></PermissionRoute>} />
              <Route path="/business-profile/:id/admins" element={<PermissionRoute permission="owner_only"><BusinessAdminsPage /></PermissionRoute>} />
              <Route path="/dashboard/business-leads" element={<PermissionRoute permission="owner_only"><BusinessLeads /></PermissionRoute>} />
              <Route path="/dashboard/business-quotes" element={<Navigate to="/dashboard/quotes?view=recibidas" replace />} />
              <Route path="/dashboard/jobs" element={<PermissionRoute permission="can_manage_jobs"><ProviderJobs /></PermissionRoute>} />
              <Route path="/dashboard/subscription" element={<Navigate to="/dashboard/subscription/actual" replace />} />
              {/* Suscripción: cualquier asociado puede ver el plan con el
                  que opera su empresa, no sólo owners con full perms. */}
              <Route path="/dashboard/subscription/:tab" element={<PermissionRoute permission="any_business_relation"><SubscriptionPage /></PermissionRoute>} />
              {/* Rutas con permiso específico del admin */}
              <Route path="/dashboard/products" element={<PermissionRoute permission="can_edit_products"><ProductsPage /></PermissionRoute>} />
              <Route path="/dashboard/services" element={<PermissionRoute permission="can_edit_services"><ServicesPage /></PermissionRoute>} />
              <Route path="/dashboard/business-reviews" element={<PermissionRoute permission="can_manage_reviews"><BusinessReviewsDashboard /></PermissionRoute>} />
              <Route path="/dashboard/analytics" element={<PermissionRoute permission="can_view_analytics"><BusinessAnalytics /></PermissionRoute>} />
              <Route path="/dashboard/blog" element={<PermissionRoute permission="can_publish_blog"><BusinessBlogPage /></PermissionRoute>} />
              {/* Rutas abiertas para cualquier usuario autenticado */}
              <Route path="/dashboard/quotes" element={<QuotesPage />} />
              <Route path="/dashboard/associations" element={<AssociationRequestPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/businesses" element={<AdminBusinesses />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/reviews" element={<AdminReviews />} />
              <Route path="/admin/leads" element={<AdminLeads />} />
              <Route path="/admin/content" element={<AdminContent />} />
              <Route path="/admin/legal" element={<AdminLegal />} />
              <Route path="/admin/faq" element={<AdminFAQ />} />
              <Route path="/admin/contact-requests" element={<AdminContactRequests />} />
              <Route path="/admin/qa-sessions" element={<AdminQASessions />} />
              <Route path="/admin/plan-requests" element={<AdminPlanRequests />} />
              <Route path="/admin/newsletter" element={<AdminNewsletter />} />
              <Route path="/admin/articles" element={<AdminBlogArticles />} />
              <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
              <Route path="/admin/platform-config" element={<Navigate to="/admin/catalog-structure" replace />} />
              <Route path="/admin/categories" element={<Navigate to="/admin/catalog-structure" replace />} />
              <Route path="/admin/catalog-structure" element={<AdminCatalogStructure />} />
              <Route path="/admin/whatsapp-community" element={<AdminWhatsAppCommunity />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/custom-fields" element={<AdminCustomFields />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/jobs" element={<AdminJobs />} />
              <Route path="/admin/quote-requests" element={<AdminQuoteRequests />} />
              <Route path="/admin/association-requests" element={<AdminAssociationRequests />} />
              <Route path="/admin/claim-requests" element={<AdminClaimRequests />} />
              <Route path="/admin/forum" element={<AdminForum />} />
              <Route path="/admin/bug-reports" element={<AdminBugReports />} />
              <Route path="/docs" element={<DocsHub />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/legal" element={<LegalIndex />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/politica-privacidad" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/terminos" element={<TermsOfService />} />
              <Route path="*" element={<NotFound />} />
            </RouterRoutes>
          </Suspense>
        </ErrorBoundary>
      </DataProvider>
    </BrowserRouter>
  );
};

export default Routes;